/** The request queue: who asked for a code, and what to do about each. The
 *  same list serves /redeem/requests (every app) and an app's own 요청 tab. */

import { useState } from 'react'
import { Inbox, RefreshCw } from 'lucide-react'
import { api, type RedeemApp, type RedeemRequest, type RedeemRequestState } from '@/api/client'
import { useRedeemApps, useRequests } from '@/lib/queries'
import { appLabel, useInvalidateRedeem } from '@/lib/redeem'
import { TONE_CLASS, statusOf } from '@/lib/status'
import { cn } from '@/lib/utils'
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FilterBar,
  FilterSelect,
  PlatformMark,
  Segmented,
  StatusPill,
  When,
  errorText,
  useQueryParam,
  useToast,
  type Column,
} from '@/ui'

const STATES: RedeemRequestState[] = ['new', 'issued', 'sent', 'replied', 'failed', 'duplicate', 'ignored']

/** What each state means for the person waiting on the other end. */
const NOTE: Record<string, string> = {
  new: '코드 요청으로 판별됨. 아직 아무것도 안 나갔다.',
  issued: '코드는 예약됐고 전송이 안 됐다. 다시 시도한다.',
  sent: 'DM은 갔고 공개 답글이 남았다.',
  replied: '끝. 코드 전달과 회신이 됐다. Bluesky는 DM 대화 안의 답이 곧 회신이다.',
  failed: '실패. 사유를 보고 다시 시도한다.',
  duplicate: '이미 이 앱의 코드를 받은 사람.',
  ignored: '코드 요청이 아니라고 판별됨(안드로이드 문의 등).',
}
const NOTE_STATES = new Set(['new', 'issued', 'sent', 'failed'])
/** A new request on an app whose automatic sending is off: nothing will move
 *  it but a person. */
export const MANUAL_NOTE = '자동 발송이 꺼진 앱이다. 보내기를 누를 때까지 기다린다.'

/** Only an app that is known to be manual: the apps list may not be in yet. */
function waitsForAPerson(app?: RedeemApp) {
  return !!app && app.auto_handout !== 1
}

/** Who asked, linked to where they asked: the comment on Reddit, the profile
 *  on Bluesky (a DM has no public link). */
function Requester({ r }: { r: RedeemRequest }) {
  const cls = 'font-medium hover:underline'
  if (r.platform === 'bluesky') {
    return (
      <a href={`https://bsky.app/profile/${r.requester_id || r.requester}`} target="_blank" rel="noreferrer" className={cls} title={r.requester_id}>
        @{r.requester}
      </a>
    )
  }
  return r.permalink ? (
    <a href={`https://www.reddit.com${r.permalink}`} target="_blank" rel="noreferrer" className={cls}>
      u/{r.requester}
    </a>
  ) : (
    <span className="font-medium">u/{r.requester}</span>
  )
}

type Ask = { kind: 'handle' | 'resend'; r: RedeemRequest; app?: RedeemApp }

/** The ended-giveaway warning. Sending is what the buttons do anyway; on an
 *  app whose giveaway is over it reaches people who were told it ended. */
export const ENDED_WARNING = '이 나눔은 끝났다.'

export function RequestsQueue({ appId = 0 }: { appId?: number }) {
  const [state, setState] = useQueryParam('state', 'all')
  const [appParam, setAppParam] = useQueryParam('app_id', '')
  const fixed = appId > 0
  const filterApp = fixed ? appId : Number(appParam) || 0
  const cur = (STATES as string[]).includes(state) ? (state as RedeemRequestState) : 'all'

  const list = useRequests(cur === 'all' ? '' : cur, filterApp, 100, true)
  const apps = (useRedeemApps().data?.items ?? []).map((i) => i.app)
  const invalidate = useInvalidateRedeem()
  const toast = useToast()
  const [busy, setBusy] = useState<number | 'watch' | null>(null)
  const [asking, setAsking] = useState<Ask | null>(null)

  const counts = list.data?.counts ?? {}
  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  const appOf = (id: number) => apps.find((a) => a.id === id)
  const appName = (id: number) => {
    const a = appOf(id)
    return a ? appLabel(a, apps) : `앱 ${id}`
  }

  // A failed send has already changed the row (the desk records `failed` and
  // the reason before it answers with an error), so the list is read again
  // either way. The toast is one short line; the server's text sits under it.
  const act = async (id: number | 'watch', fn: () => Promise<unknown>, done?: string, failed = '처리하지 못했다') => {
    setBusy(id)
    try {
      await fn()
      await invalidate()
      if (done) toast.success(done)
    } catch (e) {
      toast.error(failed, errorText(e))
      await invalidate().catch(() => {})
    } finally {
      setBusy(null)
    }
  }

  const run = (a: Ask) =>
    a.kind === 'handle'
      ? act(a.r.id, () => api.handleRedeemRequest(a.r.id), '처리했다', '코드를 보내지 못했다. 사유는 요청 줄에 적혀 있다.')
      : act(a.r.id, () => api.resendRedeemRequest(a.r.id), '같은 코드를 다시 보냈다', '다시 보내지 못했다. 사유는 요청 줄에 적혀 있다.')

  // Send and resend ask first when the giveaway has ended.
  const request = (a: Ask) => {
    const app = appOf(a.r.app_id)
    if (app?.ended_at) setAsking({ ...a, app })
    else void run(a)
  }

  const watch = () =>
    act('watch', async () => {
      const res = await api.redeemWatchNow()
      const bits = Object.entries(res.counts ?? {})
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${k} ${n}`)
      toast.success(bits.length ? `받은편지함을 읽었다 · ${bits.join(' · ')}` : '받은편지함을 읽었다')
    }, undefined, '받은편지함을 읽지 못했다')

  const columns: Column<RedeemRequest>[] = [
    {
      key: 'who',
      header: '요청자',
      card: 'title',
      cell: (r) => (
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5">
            <PlatformMark platform={r.platform} />
            <Requester r={r} />
          </span>
          <div className="text-meta font-normal text-muted-foreground">
            <When iso={r.requested_at} />
          </div>
        </div>
      ),
    },
    ...(fixed
      ? []
      : [{ key: 'app', header: '앱', cell: (r: RedeemRequest) => appName(r.app_id) } satisfies Column<RedeemRequest>]),
    {
      key: 'body',
      header: '본문',
      className: 'max-w-[26rem] align-top',
      cell: (r) => (
        <div className="space-y-1">
          <div className="line-clamp-2 break-words">{r.body}</div>
          {r.error_msg && <div className="whitespace-pre-wrap break-words font-mono text-meta text-status-failed">{r.error_msg}</div>}
          {NOTE_STATES.has(r.state) && (
            <div className="text-meta text-muted-foreground">
              {r.state === 'new' && waitsForAPerson(appOf(r.app_id)) ? (
                <span className={cn('font-medium', TONE_CLASS.publishing.text)}>{MANUAL_NOTE}</span>
              ) : (
                NOTE[r.state]
              )}
            </div>
          )}
        </div>
      ),
    },
    { key: 'state', header: '상태', card: 'meta', cell: (r) => <StatusPill domain="request" status={r.state} /> },
    {
      key: 'actions',
      header: '처리',
      align: 'right',
      card: 'actions',
      className: 'align-top',
      cell: (r) => {
        const own = busy === r.id
        const off = busy !== null
        return (
          <div className="flex flex-wrap justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
            {(r.state === 'new' || r.state === 'issued' || r.state === 'sent' || r.state === 'failed') && (
              // Filled only where the row waits on the operator: a new request,
              // a code reserved but not sent, a failed send. Retrying one that
              // went out is a fallback, not the next step, so it stays outlined.
              <Button
                size="sm"
                variant={r.state === 'sent' ? 'secondary' : 'primary'}
                loading={own}
                disabled={off}
                onClick={() => request({ kind: 'handle', r })}
              >
                {r.state === 'new' ? '코드 보내기' : '다시 시도'}
              </Button>
            )}
            {/* A delivery can fail without saying so, and then the row reads
                "완료" while the person has nothing. Resending uses the code
                already reserved for them. */}
            {(r.state === 'replied' || r.state === 'sent') && r.code_id > 0 && (
              <Button size="sm" disabled={off} loading={own} onClick={() => request({ kind: 'resend', r })} title="같은 코드를 다시 보낸다. 새 코드를 쓰지 않는다.">
                재전송
              </Button>
            )}
            {/* The DM check can read a delivered message as stuck; when the
                person did get it, the desk finishes the handout as a confirmed
                send would — the public receipt, then done. No second DM. */}
            {r.state === 'failed' && r.code_id > 0 && (
              <Button
                size="sm"
                disabled={off}
                loading={own}
                onClick={() =>
                  act(
                    r.id,
                    async () => {
                      const res = await api.setRedeemRequestState(r.id, 'sent')
                      if (res.reply_error) throw new Error(res.reply_error)
                    },
                    '답글을 달고 완료로 닫았다',
                    '전달됨으로 기록했지만 답글을 달지 못했다',
                  )
                }
                title="실제로는 DM이 갔을 때. 같은 코드를 그대로 두고, 댓글에 보냈다는 답글을 단 뒤 완료로 닫는다. DM은 다시 보내지 않는다."
              >
                보냈음
              </Button>
            )}
            {r.state === 'new' && (
              <Button size="sm" disabled={off} onClick={() => act(r.id, () => api.setRedeemRequestState(r.id, 'ignored'), '제외했다')}>
                제외
              </Button>
            )}
            {r.state === 'ignored' && (
              <Button size="sm" disabled={off} onClick={() => act(r.id, () => api.setRedeemRequestState(r.id, 'new'), '대기로 되돌렸다')}>
                대기로 되돌리기
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <FilterBar
        end={
          <Button size="sm" icon={RefreshCw} loading={busy === 'watch'} disabled={busy !== null} onClick={() => void watch()} title="3분 주기를 기다리지 않고 지금 받은편지함(Reddit·Bluesky DM)을 읽는다">
            지금 읽기
          </Button>
        }
      >
        <Segmented<string>
          label="요청 상태"
          value={cur}
          onChange={setState}
          options={[
            { value: 'all', label: '전체', count: total },
            ...STATES.map((s) => ({ value: s, label: statusOf('request', s).label, count: counts[s] ?? 0 })),
          ]}
        />
        {!fixed && (
          <FilterSelect
            label="앱"
            value={appParam}
            onChange={setAppParam}
            options={[{ value: '', label: '앱 전체' }, ...apps.map((a) => ({ value: String(a.id), label: appLabel(a, apps) }))]}
          />
        )}
      </FilterBar>

      <DataTable
        label="요청 목록"
        columns={columns}
        rows={list.data?.items ?? (list.isPending ? undefined : [])}
        loading={list.isPending}
        error={list.error && !list.data ? list.error : undefined}
        onRetry={() => list.refetch()}
        rowKey={(r) => r.id}
        empty={
          <EmptyState
            icon={Inbox}
            title="이 조건의 요청이 없다"
            description={`${cur === 'all' ? '전체' : statusOf('request', cur).label}${filterApp ? ` · ${appName(filterApp)}` : ''}`}
          />
        }
      />

      <ConfirmDialog
        open={!!asking}
        onOpenChange={(o) => !o && busy === null && setAsking(null)}
        title={asking?.kind === 'resend' ? '끝난 나눔의 코드를 다시 보낼까?' : '끝난 나눔의 요청을 처리할까?'}
        description={
          asking && (
            <>
              <span className="block font-medium text-status-failed">{ENDED_WARNING}</span>
              <span className="mt-2 block">
                {appName(asking.r.app_id)} · {asking.r.platform === 'bluesky' ? '@' : 'u/'}
                {asking.r.requester}
                {asking.kind === 'resend'
                  ? ' — 이미 받은 코드를 같은 사람에게 한 번 더 보낸다.'
                  : ' — 코드는 나가지 않는다. 종료됐다는 답글(Reddit은 공개 답글, Bluesky는 대화) 한 줄만 올라가고 요청은 제외로 닫힌다.'}
              </span>
            </>
          )
        }
        confirmLabel={asking?.kind === 'resend' ? '그래도 보내기' : '종료 답글 달기'}
        confirmVariant="destructive"
        loading={busy !== null}
        onConfirm={async () => {
          if (!asking) return
          const a = asking
          setAsking(null)
          await run(a)
        }}
      />
    </div>
  )
}
